import type { PurchasesPackage } from 'react-native-purchases'
import React, {
  createContext,
  ReactNode,
  use,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react'
import Constants from 'expo-constants'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import type { User as BackendUser } from '../dal/user-dal'
import type { RevenueCatInitStatus } from '../hooks/revenuecat-init-controller'
import type { PurchaseResult } from '../hooks/useRevenueCatPurchaseResult'
import type {
  AccountRouteDecision,
  AuthStatus,
  BackendUserStatus,
  ProtectedRouteDecision,
} from './app-session-state'
import { useResolvedAuthState } from '../hooks/useResolvedAuthState'
import { useRevenueCat } from '../hooks/useRevenueCat'
import { customerInfoQueryOptions } from '../queries/account-queries'
import { userQueryOptions } from '../queries/user-queries'
import {
  hasBackendEntitlement,
  hasResolvedAccess,
} from '../services/entitlement-utils'
import {
  getAuthErrorKind,
  isIgnorableAuthSessionError,
} from '../utils/auth-errors'
import { addBreadcrumb, captureException } from '../utils/sentry'
import { clearLocalSessionState } from '../utils/session-cleanup'
import { isRevenueCatTestStore } from './app-session-config'
import {
  hasRequiredBackendUserIds,
  resolveAccessPending,
  resolveAccountRoute,
  resolveBackendUserStatus,
  resolveProtectedRoute,
  resolveRequiresPostPaymentOnboarding,
  resolveStorageScope,
} from './app-session-state'
import { useAppSessionAuthEffects } from './useAppSessionAuthEffects'
import { useStorageScopeSynchronization } from './useStorageScopeSynchronization'

interface AppSessionContextValue {
  backendUser: BackendUser | null | undefined
  backendUserError: unknown
  backendUserStatus: BackendUserStatus
  protectedRoute: ProtectedRouteDecision
  accountRoute: AccountRouteDecision
  needsUserCreation: boolean
  hasPurchased: boolean
  isAccessPending: boolean
  requiresPostPaymentOnboarding: boolean
  isLoading: boolean
  paywallSkipped: boolean
  revenueCatInitStatus: RevenueCatInitStatus
  revenueCatInitError: Error | null
  isRevenueCatReady: boolean
  isRevenueCatConfigured: boolean
  isRevenueCatTestStore: boolean
  retryRevenueCatInitialization: () => Promise<void>
  logout: () => Promise<void>
  purchase: (pkg: PurchasesPackage) => Promise<PurchaseResult>
  refreshUserStatus: () => Promise<void>
  skipPaywall: () => void
}

const AppSessionContext = createContext<AppSessionContextValue | undefined>(
  undefined,
)

export function AppSessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const {
    user,
    error,
    clearSession,
    authUserId,
    isAuthenticated,
    isAuthLoading,
    getAccessToken,
  } = useResolvedAuthState()

  const appVersion =
    (Constants.expoConfig?.extra?.build as { version?: string } | undefined)
      ?.version ?? undefined

  const [syncedStorageScopeKey, setSyncedStorageScopeKey] = useState<
    string | null | undefined
  >(undefined)
  const [paywallSkipped, setPaywallSkipped] = useReducer(() => true, false)

  useEffect(() => {
    if (error != null) {
      console.error('Auth0 Error:', error)
      captureException(error, { feature: 'auth', tags: { flow: 'init' } })
    }
  }, [error])

  const authStatus: AuthStatus = isAuthLoading
    ? 'loading'
    : isAuthenticated
      ? 'authenticated'
      : 'anonymous'

  const storageScope = resolveStorageScope({
    authStatus,
    authUserId,
    syncedStorageScopeKey,
  })

  const {
    targetStorageScopeKey,
    nextScopeUserId,
    isReady: isStorageScopeReady,
    shouldFetchBackendUser,
  } = storageScope

  const {
    data: backendUser,
    error: backendUserError,
    isPending: isBackendUserPending,
    isFetching: isBackendUserFetching,
  } = useQuery({
    ...userQueryOptions(authUserId, getAccessToken),
    enabled: shouldFetchBackendUser,
  })

  const backendUserStatus = resolveBackendUserStatus({
    shouldFetchBackendUser,
    backendUser,
    backendUserError,
    isPending: isBackendUserPending,
    isFetching: isBackendUserFetching,
  })
  const needsUserCreation =
    authStatus === 'authenticated' && backendUserStatus === 'missing'
  const shouldInitializeRevenueCat =
    shouldFetchBackendUser && backendUserStatus === 'ready'

  const {
    revenueCatInitStatus,
    revenueCatInitError,
    isRevenueCatReady,
    isRevenueCatConfigured,
    purchase,
    refreshUserStatus,
    retryInitialization,
    safeRevenueCatLogout,
  } = useRevenueCat({
    user,
    revenueCatId: backendUser?.revenueCatId ?? null,
    mixpanelId: backendUser?.mixpanelId ?? null,
    shouldInitialize: shouldInitializeRevenueCat,
    queryClient,
    getAccessToken,
  })

  const {
    data: customerInfo,
    isPending: isCustomerInfoPending,
    isFetching: isCustomerInfoFetching,
  } = useQuery({
    ...customerInfoQueryOptions(authUserId),
    enabled: shouldInitializeRevenueCat && revenueCatInitStatus === 'ready',
  })

  const hasPurchased = useMemo(() => {
    return hasResolvedAccess({
      customerInfo,
      entitlement: backendUser?.entitlement,
      revenueCatId: backendUser?.revenueCatId,
    })
  }, [backendUser?.entitlement, backendUser?.revenueCatId, customerInfo])

  const isAccessPending = resolveAccessPending({
    backendUserStatus,
    hasBackendEntitlement: hasBackendEntitlement(backendUser?.entitlement),
    revenueCatInitStatus,
    isCustomerInfoFetching:
      shouldInitializeRevenueCat &&
      revenueCatInitStatus === 'ready' &&
      (isCustomerInfoPending || isCustomerInfoFetching),
  })

  const backendUserIdsReady = hasRequiredBackendUserIds(backendUser)

  const requiresPostPaymentOnboarding = resolveRequiresPostPaymentOnboarding({
    hasPurchased,
    backendUser,
  })

  const logout = useCallback(async () => {
    try {
      await clearSession()
      await clearLocalSessionState({
        authUserId,
        queryClient,
        resetRevenueCat: safeRevenueCatLogout,
      })
    } catch (e) {
      if (getAuthErrorKind(e) === 'missing-credentials') {
        await clearLocalSessionState({
          authUserId,
          queryClient,
          resetRevenueCat: safeRevenueCatLogout,
        })
        addBreadcrumb(
          'auth',
          'logout_completed_missing_credentials',
          {},
          'info',
        )
        return
      }

      if (isIgnorableAuthSessionError(e)) {
        addBreadcrumb(
          'auth',
          'logout_cancelled_before_clear_local_state',
          {},
          'info',
        )
        return
      }

      console.error('Logout failed', e)
      captureException(e, { feature: 'auth', tags: { flow: 'logout' } })
      throw e
    }
  }, [authUserId, clearSession, queryClient, safeRevenueCatLogout])

  const protectedRoute = !isStorageScopeReady
    ? 'loading'
    : resolveProtectedRoute({
        authStatus,
        backendUserStatus,
        needsUserCreation,
      })
  const skipPaywall = useCallback(() => {
    setPaywallSkipped()
  }, [])

  const paywallSkippedForRoute =
    authStatus === 'authenticated' && !hasPurchased && paywallSkipped

  const accountRoute = resolveAccountRoute({
    protectedRoute,
    requiresPostPaymentOnboarding,
    isAccessPending,
    hasPurchased,
    backendUserIdsReady,
    paywallSkipped: paywallSkippedForRoute,
  })

  const isLoading =
    protectedRoute === 'loading' ||
    (shouldFetchBackendUser && isBackendUserPending)

  useAppSessionAuthEffects({
    appVersion,
    backendUser,
    backendUserStatus,
    isAuthenticated,
    user,
  })
  useStorageScopeSynchronization({
    authStatus,
    authUserId,
    isAuthenticated,
    isStorageScopeReady,
    nextScopeUserId,
    queryClient,
    setSyncedStorageScopeKey,
    targetStorageScopeKey,
  })

  const value = useMemo<AppSessionContextValue>(
    () => ({
      backendUser,
      backendUserError,
      backendUserStatus,
      protectedRoute,
      accountRoute,
      needsUserCreation,
      hasPurchased,
      isAccessPending,
      requiresPostPaymentOnboarding,
      isLoading,
      paywallSkipped: paywallSkippedForRoute,
      revenueCatInitStatus,
      revenueCatInitError,
      isRevenueCatReady,
      isRevenueCatConfigured,
      isRevenueCatTestStore,
      retryRevenueCatInitialization: retryInitialization,
      logout,
      purchase,
      refreshUserStatus,
      skipPaywall,
    }),
    [
      backendUser,
      backendUserError,
      backendUserStatus,
      accountRoute,
      hasPurchased,
      isAccessPending,
      isLoading,
      paywallSkippedForRoute,
      isRevenueCatConfigured,
      isRevenueCatReady,
      logout,
      needsUserCreation,
      protectedRoute,
      purchase,
      requiresPostPaymentOnboarding,
      refreshUserStatus,
      retryInitialization,
      revenueCatInitError,
      revenueCatInitStatus,
      skipPaywall,
    ],
  )

  return (
    <AppSessionContext.Provider value={value}>
      {children}
    </AppSessionContext.Provider>
  )
}

export function useAppSession() {
  const context = use(AppSessionContext)
  if (!context) {
    throw new Error('useAppSession must be used within an AppSessionProvider')
  }
  return context
}
